import {FC, useState, useRef, ChangeEvent, useContext} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {useSetRecoilState} from 'recoil';
import {useParams} from 'react-router-dom';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFnsV3';
import {ru} from 'date-fns/locale';
import {OverlayRenderProps} from 'react-photo-view/dist/types';

import {Button, Modal, Typography, Toolbar, IconButton} from '@mui/material';
import {Box, Stack} from '@mui/system';
import LoadingButton from '@mui/lab/LoadingButton';
import ControlledInput from '../../../../ui/controlled-input/ControlledInput';
import {DateTimePicker, LocalizationProvider} from '@mui/x-date-pickers';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

import {useFileUpload} from '../../../../hooks/useFileUpload';

import {green} from '@mui/material/colors';
import {colors} from '../../../../shared/colors';

import {EventBusContext} from '../../../../providers/EventBus';
import {triggerReasonDelays} from '../../../../store/service-requests/atoms';
import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';
import {PhotoProvider} from 'react-photo-view';
import {DelayImage} from './TableBody/DelayImage';
import {normalizeDelayDate} from './utils';
import {IServiceRequest} from '../../../../shared/types/serviceRequest.types';

interface IEditDelayForm {
  date: Date | null | undefined;
  comment?: string;
}

interface IEditDelay {
  open: boolean;
  handleClose: () => void;
  delay: Record<string, any>;
  serviceRequest: IServiceRequest;
}

const style = {
  position: 'absolute' as const,
  width: 600,
  maxHeight: 800,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
};

const ToolBar: FC<OverlayRenderProps> = ({onScale, scale}) => {
  return (
    <>
      <IconButton onClick={() => onScale(scale + 1)}>
        <ZoomInIcon htmlColor="#fff" />
      </IconButton>
      <IconButton onClick={() => onScale(scale - 1)}>
        <ZoomOutIcon htmlColor="#fff" />
      </IconButton>
    </>
  );
};

export const EditDelay: FC<IEditDelay> = ({open, handleClose, delay, serviceRequest}) => {
  const {control, reset, trigger, watch, handleSubmit} = useForm<IEditDelayForm>({
    defaultValues: {
      date: delay.date ? normalizeDelayDate(delay.date) : null,
    },
  });
  const {files, fileNames, setFiles} = useFileUpload();
  const {files: files2, fileNames: fileNames2, setFiles: setFiles2} = useFileUpload();
  const {id} = useParams();
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState<any>();
  const triggerQuery = useSetRecoilState(triggerReasonDelays);
  const {bus} = useContext(EventBusContext);

  const inputRef = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFiles(e);
  };

  const onChange2 = (e: ChangeEvent<HTMLInputElement>) => {
    setFiles2(e);
  };

  const onSubmit: SubmitHandler<IEditDelayForm> = async (data) => {
    setLoading(true);
    try {
      const date = new Date(data.date as Date);

      date.setTime(date.getTime() + (date.getTimezoneOffset() + 180) * -1 * 60 * 1000);

      await ServiceRequestService.editReasonDelay(serviceRequest.id, Number(delay.id), {
        photos: files[0] || files2[0] ? {docFile: files[0], docFile2: files2[0]} : null,
        comment: data.comment ? data.comment.trim() : '',
        date: date.toISOString(),
      });

      setLoading(false);
      triggerQuery((v) => v + 1);
      reset();
      handleClose();
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const watchDate = watch('date');

  console.log(watchDate, watchDate && new Date(watchDate).toString());

  return (
    <Modal open={open}>
      <Box sx={style}>
        <Toolbar
          sx={{
            pl: {sm: 2},
            pr: {xs: 1, sm: 1},
            bgcolor: colors['bg--primary'],
          }}
        >
          <Typography variant="h6" component="div">
            {`Недопуск №${delay.id}`}
          </Typography>
        </Toolbar>
        <Stack sx={{p: '16px'}} direction="column" spacing={5}>
          <Stack direction="column" spacing={2}>
            <Controller
              control={control}
              name="date"
              render={({field: {value, onChange}}) => (
                <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Дата недопуска"
                    format={'dd.MM.yyyy HH:mm'}
                    value={value}
                    onError={(reason) => setDateError(reason)}
                    minutesStep={1}
                    disableFuture
                    onChange={onChange}
                  />
                </LocalizationProvider>
              )}
            />
            <Typography variant="body2" sx={{opacity: 0.8}}>
              Уведомление*
            </Typography>
            <Stack sx={{justifyContent: 'space-between'}} direction="row" spacing={2}>
              <Stack direction="row" alignItems={'center'} spacing={1}>
                <PhotoProvider
                  overlayRender={({images, index}) => {
                    return (
                      <Stack
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          width: '100%',
                          padding: '.5rem',
                          zIndex: 50,
                          background: '#00000080',
                        }}
                      >
                        <Stack>{images[index].overlay}</Stack>
                      </Stack>
                    );
                  }}
                  toolbarRender={(props) => <ToolBar {...props} />}
                >
                  <DelayImage photo={files[0] || delay?.document} label={'Фото уведомления'} />
                </PhotoProvider>
              </Stack>
              <Button
                onClick={() => inputRef?.current?.click()}
                sx={{
                  height: '40px',
                  fontSize: '0.87rem',
                  letterSpacing: '0',
                  backgroundColor: green[500],
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: green[600],
                  },
                }}
                variant="contained"
              >
                {!fileNames.length ? 'Выбрать фото' : 'Выбрать другое'}
              </Button>
            </Stack>
            <Typography variant="body2" sx={{opacity: 0.8}}>
              Акт недопуска
            </Typography>
            <Stack sx={{justifyContent: 'space-between'}} direction="row" spacing={2}>
              <Stack direction="row" alignItems={'center'} spacing={1}>
                <PhotoProvider
                  overlayRender={({images, index}) => {
                    return (
                      <Stack
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          width: '100%',
                          padding: '.5rem',
                          zIndex: 50,
                          background: '#00000080',
                        }}
                      >
                        <Stack>{images[index].overlay}</Stack>
                      </Stack>
                    );
                  }}
                  toolbarRender={(props) => <ToolBar {...props} />}
                >
                  <DelayImage photo={files2[0] || delay?.document2} label={'Фото акта недопуска'} />
                </PhotoProvider>
              </Stack>
              <Button
                onClick={() => inputRef2?.current?.click()}
                sx={{
                  height: '40px',
                  fontSize: '0.87rem',
                  letterSpacing: '0',
                  backgroundColor: green[500],
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: green[600],
                  },
                }}
                variant="contained"
              >
                {!fileNames2.length ? 'Выбрать фото' : 'Выбрать другое'}
              </Button>
            </Stack>
          </Stack>
          <ControlledInput
            control={control}
            name="comment"
            rules={{
              maxLength: {
                value: 400,
                message: 'Максимум 400 символов',
              },
            }}
            multiline
            rows={4}
            label="Комментарий"
            size="small"
            additionalFunc={() => {
              trigger('comment');
            }}
          />
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Button onClick={handleClose} color="error" variant="outlined">
              Отмена
            </Button>
            <LoadingButton
              disabled={!(!!watchDate && new Date(watchDate).toString() !== 'Invalid Date')}
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              loading={loading}
            >
              Сохранить
            </LoadingButton>
          </Stack>
          <input
            type="file"
            ref={inputRef}
            accept=".jpg,.jpeg,.png,.webp"
            style={{display: 'none'}}
            onChange={onChange}
          />
          <input
            type="file"
            ref={inputRef2}
            accept=".jpg,.jpeg,.png,.webp"
            style={{display: 'none'}}
            onChange={onChange2}
          />
        </Stack>
      </Box>
    </Modal>
  );
};
